// client/src/services/lexistarMedia.ts
//
// LexiStar driver media (ST-00188; WhatsApp runbook §5.8): the photos a driver
// sends with a deviation reason. The server streams a stored photo from MinIO
// at GET /api/lexistar/media?ref=s3://… (lexistar.trips.view, own tenant's
// photos only); there are no signed URLs, so the page fetches it through the
// shared axios instance (cookie session, CSRF, 401 refresh — services/api) and
// shows it from a blob: URL.

import api from './api';
import type { DriverMediaItem, DriverMediaLike } from '../types/lexistar-media';

/** A stored photo's ref starts with this; anything else is a provider media id (not viewable). */
export const STORED_MEDIA_PREFIX = 's3://';

export const isStoredMediaRef = (ref: unknown): ref is string =>
  typeof ref === 'string' && ref.startsWith(STORED_MEDIA_PREFIX);

/**
 * GET /lexistar/media?ref= → the photo's bytes. Rejects on any HTTP error (400
 * malformed ref, 403 another organisation's photo, 404 gone, 5xx store down)
 * and when the body is not a Blob.
 */
export async function fetchLexiStarMedia(
  ref: string,
  opts: { signal?: AbortSignal } = {}
): Promise<Blob> {
  const res = await api.get<Blob>('/lexistar/media', {
    params: { ref },
    responseType: 'blob',
    timeout: 60000,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  const data: unknown = res.data;
  if (!(data instanceof Blob)) throw new Error('The photo did not arrive as a file');
  return data;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';

/**
 * The driver's photos of an approvals-queue row: `driver_reason_media` (the
 * API's list), else — from a server that predates it — `driver_reason.media`.
 * Entries without a ref are dropped.
 */
export function driverPhotosOf(row: {
  driver_reason_media?: unknown;
  driver_reason?: unknown;
}): DriverMediaLike[] {
  const list: unknown = Array.isArray(row.driver_reason_media)
    ? row.driver_reason_media
    : isRecord(row.driver_reason)
      ? row.driver_reason.media
      : null;
  if (!Array.isArray(list)) return [];
  const out: DriverMediaLike[] = [];
  for (const m of list) {
    if (!isRecord(m) || typeof m.ref !== 'string' || !m.ref) continue;
    const item: DriverMediaLike = { ref: m.ref };
    if (typeof m.kind === 'string') item.kind = m.kind;
    if (typeof m.mime_type === 'string') item.mime_type = m.mime_type;
    if (typeof m.bytes === 'number') item.bytes = m.bytes;
    if (typeof m.sha256 === 'string') item.sha256 = m.sha256;
    out.push(item);
  }
  return out;
}

export type { DriverMediaItem, DriverMediaLike };
